// The operations desk: who gets the firm's back-office and compliance work.
//
// WHY THIS FILE EXISTS (Grant, 2026-10-17; build step A1 of
// docs/design/commissions-build-program.md). Brian Baker will leave the COO
// and compliance role to become a Planning advisor, and he stays an active
// user. CORRECTION (Grant, 2026-10-18): he has NOT left yet, so for now the
// desk is Baker AND Grant, equally (`shared: true` on both). When his role
// change is scheduled, removing his line from OPS_DESK_MEMBERS is the whole
// edit. Many jobs, cleanup lanes, inbox fallbacks and on-screen messages
// found him by his email or his name and only checked "is the account
// active", so after his role changed all of that would have kept landing on
// him with nothing to flag it. Every one of those places now reads THIS
// list instead. Changing who the desk is, is a one-line edit here.
//
// WHAT "ON THE DESK" MEANS: a person who may receive an item. It is not
// approval authority (that stays Grant plus the per-company backups,
// commissions-build-program.md section 7.4). Cambria and Donna are listed for
// named clerical duties only.
//
// DECISION: members are matched by EMAIL, never by a hard-coded user id,
// because ids differ per environment (invite flow) and a reseed must not
// strand the routing. Same convention as shared/judgment/catalog.ts.
//
// DECISION: a duty not listed against anyone goes to the PRIMARY. A member
// listed for a duty gets it first and the primary is the fallback if that
// person is inactive, so no duty is ever left without an owner while Grant's
// account is active.
//
// DAY-ONE ROUTING (Grant, 2026-10-17: "use your recommendations"): Cambria
// and Donna take only what they can finish in the OS TODAY. The handover
// (docs/design/coo-handover.md section 4) also names them for these, but
// those screens are admin-only until its step 2 opens them, so for now
// these stay with Grant. Opening one is a one-line edit: add the duty to the
// member's `duties` below.
//   Cambria  <- "onlineed-verification"  (CE and OnlineEd verification)
//   Cambria  <- "retention-review"       (recommends; Grant still decides)
//   Donna    <- "license-renewal"        (files in the portal, binder, scan)
//   Donna    <- "realty-acknowledgments" (sends and chases in September)

/**
 * Every kind of item the desk receives. Add one when a new site routes here.
 * A const array (not just a union) so the acceptance test can walk every
 * duty and a new one cannot be forgotten.
 */
export const OPS_DESK_DUTIES = [
  /** January licence renewal tasks (one per expiring licence). */
  "license-renewal",
  /** September Realty Broker Manual, Commission Agreement and ICA tasks. */
  "realty-acknowledgments",
  /** The task asking the desk to verify a loan officer's OnlineEd completion. */
  "onlineed-verification",
  /** Advisor Requests with no named recipient. */
  "advisor-requests",
  /** A commission disbursement pending over seven days. */
  "commission-stale",
  /** A carrier consolidation reaching the money. */
  "carrier-consolidation",
  /** A document past its retention period: keep, hold or destroy. */
  "retention-review",
  /** Realty cutover reconciliation (service record needs household, duplicate transaction). */
  "realty-cutover-reconciliation",
  /** Two or more identical Mortgage stub households a person must settle. */
  "mortgage-stub-review",
  /** Contact-level judgment items whose household has no serving advisor. */
  "contacts-households-backstop",
  /** Money exceptions from the money ledger: overdue, short, unplaced, a feed overdue. */
  "money-exceptions",
  /** A comp plan form out for signature and not yet signed (build step B2). */
  "comp-plan-signatures",
  /** Pay runs: an earning held for a decision, a pay rule found broken (build step B3). */
  "pay-runs",
  /** The John Hancock 401(k) contribution after each payroll (build program 9.5). */
  "retirement-contributions",
  /** Pay statements: a question waiting for a reply, a payroll change asked for (build step B5). */
  "pay-statements",
] as const;

export type OpsDeskDuty = (typeof OPS_DESK_DUTIES)[number];

export interface OpsDeskMember {
  email: string;
  /** Full name, as the team reads it. */
  name: string;
  /** The primary is the last-resort fallback and the name in "Ask Grant" wording. */
  primary?: boolean;
  /**
   * An equal owner of the whole desk (Grant, 2026-10-18: Baker and Grant for
   * now). Every shared member sees and may act on every desk item, including
   * Advisor Requests. Where an item can carry ONE name, the first shared
   * member who claims the duty gets it; the other still sees it.
   */
  shared?: boolean;
  /** Duties this member takes ahead of the primary. */
  duties?: readonly OpsDeskDuty[];
}

/**
 * The duties Cambria and Donna take ahead of the shared owners. Everything
 * else is claimed by the shared owners (see BAKER below).
 */
const CLERICAL_CLAIMS: readonly OpsDeskDuty[] = [
  "mortgage-stub-review",
  "contacts-households-backstop",
];

/**
 * DECISION (Grant's lean, 2026-10-18): where an item holds ONE name (a task,
 * a cleanup item's responsible person), Baker's goes on it, because he does
 * the work today; Grant is the fallback and still sees it. Nothing is ever
 * created twice to give each a copy. Taking Baker off the desk later moves
 * these to Grant by deleting his line.
 */
const SHARED_OWNER_DUTIES: readonly OpsDeskDuty[] = OPS_DESK_DUTIES.filter(
  (d) => !CLERICAL_CLAIMS.includes(d),
);

/** The list. Order matters only among members who share a duty. */
export const OPS_DESK_MEMBERS: readonly OpsDeskMember[] = [
  {
    email: "baker@stewardship.pro",
    name: "Brian Baker",
    shared: true,
    duties: SHARED_OWNER_DUTIES,
  },
  { email: "grant@stewardship.pro", name: "Grant Botma", primary: true, shared: true },
  {
    email: "cambria@stewardship.pro",
    name: "Cambria Botma",
    duties: ["mortgage-stub-review"],
  },
  {
    email: "donna@stewardship.pro",
    name: "Donna Rivituso",
    duties: ["contacts-households-backstop"],
  },
];

export const OPS_DESK_PRIMARY: OpsDeskMember = (() => {
  const primary = OPS_DESK_MEMBERS.find((m) => m.primary);
  if (!primary) throw new Error("ops-desk: the member list has no primary");
  return primary;
})();

// ─── Wording the team reads ──────────────────────────────────────────
// One place, so "Ask Grant or Brian Baker" stops being a sentence nobody
// updates. The Stew system prompt is byte-stable across users (prompt
// cache), and these are module constants, so using them there is safe.

/** The first name of the person to ask: "Ask Grant." */
export const OPS_DESK_ASK: string = OPS_DESK_PRIMARY.name.split(" ")[0] ?? OPS_DESK_PRIMARY.name;

/** The desk as a noun, for sentences about the role rather than a person. */
export const OPS_DESK_LABEL = "the operations desk";

/** "the operations desk (Grant)": for owner labels and job descriptions. */
export const OPS_DESK_LABEL_WITH_PRIMARY = `${OPS_DESK_LABEL} (${OPS_DESK_ASK})`;

// ─── Pure helpers ────────────────────────────────────────────────────

/**
 * Member emails in the order they should be tried for `duty`: members who
 * list the duty first (list order), then the primary, then everyone else.
 * Pure, so it can be tested and used wherever a caller resolves emails to
 * user ids itself (shared/db/judgment-routing.ts does).
 */
export function opsDeskEmailsForDuty(duty: OpsDeskDuty): string[] {
  const claimed = OPS_DESK_MEMBERS.filter((m) => m.duties?.includes(duty));
  const rest = OPS_DESK_MEMBERS.filter((m) => !claimed.includes(m));
  // Members who did not claim the duty come last: they receive it only if
  // the claimants AND the primary are all unusable, so a deactivated primary
  // never strands an item. While the primary is active they never see it.
  return [
    ...claimed,
    ...rest.filter((m) => m.primary),
    ...rest.filter((m) => !m.primary),
  ].map((m) => m.email);
}

/**
 * Emails of the equal desk owners (Baker and Grant today). Everything the
 * desk receives is visible to, and actionable by, every one of these, even
 * where an item can carry only one name.
 */
export function opsDeskSharedEmails(): string[] {
  return OPS_DESK_MEMBERS.filter((m) => m.shared).map((m) => m.email);
}

/**
 * Desk items (cleanup detector slugs) that land in EVERY equal desk owner's
 * lane, not only on the one name the item carries (Grant, 2026-10-23: the
 * John Hancock contribution "must also land as Grant's own to-do"). Still
 * one item: clearing it once clears it for both. Read by myLaneWhere
 * (shared/db/cleanup-lanes.ts). Adding a slug here is the whole edit.
 */
export const OPS_DESK_SHARED_ITEM_SLUGS: readonly string[] = ["retirement-contribution-due"];

/** True when this email is an equal desk owner (case-insensitive). */
export function isOpsDeskSharedEmail(email: string | null | undefined): boolean {
  const e = (email ?? "").trim().toLowerCase();
  return e !== "" && opsDeskSharedEmails().includes(e);
}

/** True when this email is on the desk list (case-insensitive). */
export function isOpsDeskEmail(email: string | null | undefined): boolean {
  const e = (email ?? "").trim().toLowerCase();
  return e !== "" && OPS_DESK_MEMBERS.some((m) => m.email === e);
}
